import { Grid } from '@mui/material';
import { LineChart as MuiLineChart } from '@mui/x-charts';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SortTypes } from '../../../../../types';
import { TableView } from '../../../../tableview/TableView';
import { useElementWidth } from '../../common/utils';
import { ChartHeader } from '../common/chart-header/ChartHeader';
import { AxisConfigType } from '../common/common-chart-types';
import {
  getChartLegendConfig,
  getChartNoDataOverlay,
} from '../common/common-chart-utils/common-chart-configs';
import {
  CHART_CONTENT_HEIGHT,
  defaultChartColors,
  getChartColorWheel,
} from '../common/common-chart-utils/common-chart-styles';
import { MarkWithLabel } from '../common/label/CustomLabel';
import { CustomAxisTooltip } from '../common/tooltip/CustomAxisTooltip';
import {
  getMarginsLineChart,
  getModifiedXAxisConfigsLineChart,
  getPlotWidthLineChart,
} from './line-chart-display-utils';
import { LineChartProps } from './line-chart-types';
import {
  getMuiLineChartDataConfigFromChartData,
  getMuiLineChartTableDataFromChartData,
} from './line-chart-utils';

export const LineChart = (props: LineChartProps) => {
  const {
    fullScreenHandle,
    titleConfig,
    displayConfig,
    chartData,
    chartColors = defaultChartColors,
  } = props;
  const selectedSortOrder = displayConfig.enableSortOrder
    ? (displayConfig.selectedSortOrder ?? SortTypes.ASC)
    : null;
  const { elementRef, elementWidth } = useElementWidth();
  const chartRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const {
    showLabels = false,
    showTableView = false,
    showLegend = false,
    showGridLines = false,
    showWrappedXAxisLabels = false,
  } = displayConfig;
  const chartColorWheel = getChartColorWheel(chartColors);
  const chartDataConfig = getMuiLineChartDataConfigFromChartData(
    chartData,
    {
      selectedSortOrder,
      showLabels,
    },
    chartColorWheel
  );
  const tableData = getMuiLineChartTableDataFromChartData(
    chartData,
    {
      selectedSortOrder,
    },
    t
  );
  const {
    xAxisConfigs,
    yAxisConfigs,
    series,
    dataValueFormatter,
    dataset,
    styles,
  } = chartDataConfig;

  const xAxisConfigsInput = {
    isHorizontal: false as const,
    xAxisConfigs: xAxisConfigs,
  };
  const modifiedXAxisConfigs = getModifiedXAxisConfigsLineChart(
    xAxisConfigsInput,
    dataset,
    elementWidth ?? 0,
    {
      wrapLabels: showWrappedXAxisLabels,
      plotWidth: getPlotWidthLineChart(
        yAxisConfigs,
        series,
        dataset,
        elementWidth ?? 0,
        showLegend
      ),
    }
  );

  const muiXAxisConfigs =
    modifiedXAxisConfigs.type === AxisConfigType.SINGLE
      ? [modifiedXAxisConfigs.firstAxisConfig]
      : [
          modifiedXAxisConfigs.firstAxisConfig,
          modifiedXAxisConfigs.secondAxisConfig,
        ];
  const muiYAxisConfigs = yAxisConfigs
    ? yAxisConfigs.type === AxisConfigType.SINGLE
      ? [yAxisConfigs.firstAxisConfig]
      : [yAxisConfigs.firstAxisConfig, yAxisConfigs.secondAxisConfig]
    : undefined;

  const modifiedDisplayConfig = {
    ...displayConfig,
    enableWrappedXAxisLabels: true,
  };

  const [showTools, setShowTools] = useState(false);
  const topAxis =
    xAxisConfigs?.type === AxisConfigType.DOUBLE
      ? (xAxisConfigs.secondAxisConfig.id as string)
      : null;

  const rightAxis =
    yAxisConfigs?.type === AxisConfigType.DOUBLE
      ? (yAxisConfigs.secondAxisConfig.id as string)
      : null;

  const margins = getMarginsLineChart(
    modifiedXAxisConfigs,
    yAxisConfigs,
    series,
    dataset,
    elementWidth ?? 0,
    showLegend
  );
  return (
    <Grid
      item
      container
      ref={elementRef}
      direction="column"
      sx={{ height: '100%' }}
      onMouseEnter={() => setShowTools(true)}
      onMouseLeave={() => setShowTools(false)}
    >
      <ChartHeader
        titleConfig={titleConfig}
        showTools={showTools}
        chartRef={chartRef}
        fullScreenHandle={fullScreenHandle}
        displayConfig={modifiedDisplayConfig}
      />
      <Grid item container sx={{ height: CHART_CONTENT_HEIGHT }}>
        {showTableView ? (
          <TableView
            tableData={tableData}
            fullScreenHandle={fullScreenHandle}
            chartHeight={CHART_CONTENT_HEIGHT}
          />
        ) : (
          <div ref={chartRef} style={{ width: '100%', height: '100%' }}>
            <MuiLineChart
              sx={styles}
              title={titleConfig.mainTitle}
              xAxis={muiXAxisConfigs}
              topAxis={topAxis}
              yAxis={muiYAxisConfigs}
              rightAxis={rightAxis}
              dataset={dataset}
              series={series}
              margin={margins}
              slotProps={{
                popper: { container: () => fullScreenHandle.node.current },
                legend: getChartLegendConfig(showLegend),
                noDataOverlay: getChartNoDataOverlay(t),
              }}
              grid={{
                vertical: showGridLines,
                horizontal: showGridLines,
              }}
              slots={{
                mark: MarkWithLabel,
                popper: CustomAxisTooltip,
              }}
              tooltip={{ trigger: 'axis' }}
            />
          </div>
        )}
      </Grid>
    </Grid>
  );
};
